import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IdCard, Plus, Trash2 } from "lucide-react";

const CATEGORIES = [
  { value: "driver_license", label: "Driver's License / Photo ID" },
  { value: "ssn_card", label: "Social Security Card" },
  { value: "auto_insurance", label: "Auto Insurance" },
  { value: "cpr_bls", label: "CPR / BLS" },
  { value: "license", label: "License" },
  { value: "certification", label: "Certification" },
  { value: "immunization", label: "Immunization" },
  { value: "other", label: "Other" },
];

const GROUPS = [
  { value: "none", label: "Standalone" },
  { value: "identity", label: "Photo ID (List B) — one of" },
  { value: "work_auth", label: "Work authorization (List C) — one of" },
  { value: "custom_1", label: "Custom group 1 — one of" },
  { value: "custom_2", label: "Custom group 2 — one of" },
];

export default function IdentityDocsConfig({ items, onChange, readonly }) {
  const update = (idx, patch) => onChange(items.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  const toggleSide = (idx, side, on) => {
    const sides = new Set(items[idx].sides || ["front"]);
    on ? sides.add(side) : sides.delete(side);
    if (!sides.size) sides.add("front");
    update(idx, { sides: ["front", "back"].filter((s) => sides.has(s)) });
  };
  const add = () => onChange([...items, { key: `custom_${Date.now().toString(36)}`, label: "New document", category: "other", sides: ["front"], required: false, enabled: true }]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="identity-docs-config">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <IdCard className="w-5 h-5 text-teal-600" />
          <h3 className="font-semibold text-slate-900">Identity Documents</h3>
          <span className="text-xs text-slate-500">({items.filter((d) => d.enabled).length} enabled)</span>
        </div>
        {!readonly && (
          <Button variant="outline" size="sm" onClick={add} className="gap-1" data-testid="add-identity-doc-btn">
            <Plus className="w-3 h-3" /> Add Document
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">
        New hires photograph these on the Background Check and Orientation links before they have a login. Documents in the same "one of" group are alternatives — any one satisfies the requirement (e.g. Social Security card or Birth certificate). A standalone List A document (passport, Permanent Resident card) covers every List B / List C group. Files land in the HR File Vault as pending review.
      </p>
      <div className="space-y-3">
        {items.map((d, idx) => (
          <div key={d.key} className={`border rounded-lg p-4 grid gap-3 md:grid-cols-[auto_1fr_160px_120px_170px_auto_auto_auto] items-center ${d.enabled ? "border-slate-200 bg-slate-50" : "border-dashed border-slate-200 opacity-70"}`} data-testid={`identity-doc-row-${d.key}`}>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <Switch checked={!!d.enabled} onCheckedChange={(v) => update(idx, { enabled: v })} disabled={readonly} data-testid={`identity-doc-enabled-${d.key}`} />
              Ask for it
            </label>
            <div>
              <Label className="text-xs">Label shown to new hire</Label>
              <Input value={d.label} onChange={(e) => update(idx, { label: e.target.value })} disabled={readonly} data-testid={`identity-doc-label-${d.key}`} />
            </div>
            <div>
              <Label className="text-xs">File vault category</Label>
              <Select value={d.category || "other"} onValueChange={(v) => update(idx, { category: v })} disabled={readonly}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">I-9 list</Label>
              <Select value={d.i9_list || "none"} onValueChange={(v) => update(idx, { i9_list: v === "none" ? null : v })} disabled={readonly}>
                <SelectTrigger className="h-9" data-testid={`identity-doc-i9-${d.key}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="a">List A</SelectItem>
                  <SelectItem value="b">List B</SelectItem>
                  <SelectItem value="c">List C</SelectItem>
                  <SelectItem value="none">Not on I-9</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">"One of" group</Label>
              <Select value={d.alt_group || "none"} onValueChange={(v) => update(idx, { alt_group: v === "none" ? null : v })} disabled={readonly}>
                <SelectTrigger className="h-9" data-testid={`identity-doc-group-${d.key}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GROUPS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-700">
              {["front", "back"].map((side) => (
                <label key={side} className="flex items-center gap-1 capitalize">
                  <Checkbox checked={(d.sides || []).includes(side)} onCheckedChange={(v) => toggleSide(idx, side, !!v)} disabled={readonly} data-testid={`identity-doc-side-${d.key}-${side}`} /> {side}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <Switch checked={!!d.required} onCheckedChange={(v) => update(idx, { required: v })} disabled={readonly || !d.enabled} data-testid={`identity-doc-required-${d.key}`} />
              Required
            </label>
            {!readonly && (
              <Button variant="ghost" size="sm" onClick={() => onChange(items.filter((_, i) => i !== idx))} className="text-red-600 hover:bg-red-50" data-testid={`identity-doc-remove-${d.key}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
